<template>
    <form
        @submit="onBeforeSubmit"
        class="form-group"
        v-bind="$attrs"
    >
        <slot
            :onValueChanged="onValueChanged"
            :onBlur="onBlur"
            :errors="publicErrors"
            :pendingValidations="pendingValidations"
            :clearErrors="clearFieldErrors"
        >
        </slot>
        <template v-if="hasNonFieldErrors">
            <NonFieldErrors :errors="nonFieldErrors" />
        </template>
        <slot name='buttons' />
    </form>
</template>

<script>
    import {
        capitalize,
        ValidationError,
    } from '@superbet-group/web.lib.utilities';
    import {
        omit as _omit,
        isEmpty as _isEmpty,
        forEach as _forEach,
    } from 'lodash';
    import Vue from 'vue';

    import { logger } from '@app/logger';

    import NonFieldErrors from './NonFieldErrors';

    /**
     * There are a few simple rules:
     *
     * 1. Async errors always have higher weight and if there is
     *    asyncError update it will overwrite potential client errors
     * 2. If Async errors have nonField errors they are handled in separate object
     * 3. All async errors are immediately in publicErrors (visible to user)
     * 4. Every field input will set/delete (set on error, delete if not error) this.errors
     *    which is internal error bin
     * 5. If isErrorVisibleWhileTyping=true every input will also set publicErrors because
     *    errors should be visible as you type
     * 6. On submit form every field is validated and error are set only for fields which
     *    already don't have error in this.errors (remember that every input always set/delete own error)
     * 7. On submit publicField are set and shown to user
     */
    export default {
        name: 'Form',
        emits: ['submit'],
        props: {
            fields: {
                type: Object,
                required: true,
            },
            // We need support for form errors which aren't one field speficia and aren't just async.
            // I would add error or rename this one to errors
            asyncErrors: {
                type: Object,
                required: false,
                default() {
                    return {};
                },
            },
            useNonFieldErrors: {
                type: Boolean,
                required: false,
                default: true,
            },
        },
        data() {
            return {
                errors: {},
                publicErrors: {},
                nonFieldErrors: {},
                pendingValidations: {},
            };
        },
        computed: {
            fieldNames() {
                return Object.keys(this.fields);
            },
            hasNonFieldErrors() {
                return this.useNonFieldErrors && !_isEmpty(this.nonFieldErrors);
            },
        },
        watch: {
            asyncErrors: {
                immediate: true,
                handler(newValue) {
                    this.nonFieldErrors = _omit(newValue, this.fieldNames);
                    this.fieldNames.forEach((name) => {
                        if (!_isEmpty(newValue[name])) {
                            const errorText = newValue[name][0];
                            this.$set(this.errors, name, capitalize(this.$t(errorText)));
                        }
                    });
                    this.publicErrors = {
                        ...this.errors,
                    };
                },
            },
        },
        methods: {
            onBeforeSubmit(e) {
                e.preventDefault();
                this.validate();
                if (!_isEmpty(this.publicErrors)) {
                    return;
                }

                this.$emit('submit');
            },
            onValueChanged(value, name) {
                const field = this.fields[name];
                this.validateField(field, value, name);

                if (field.isErrorVisibleWhileTyping) {
                    this.publicErrors = {
                        ...this.errors,
                    };
                }
            },
            async onBlur(name, value, isDirty) {
                if (!isDirty) {
                    return;
                }
                const field = this.fields[name];
                this.validateField(field, value, name);

                // go to server only if no local errors
                if (field.asyncValidate && !this.errors[name]) {
                    Vue.set(this.pendingValidations, name, true);
                    try {
                        await field.asyncValidate(name, value);
                        Vue.set(this.pendingValidations, name, false);
                    } catch (e) {
                        if (e instanceof ValidationError) {
                            this.$set(this.errors, name, e.fieldErrors[name][0]);
                        } else {
                            logger.error(e);
                        }
                        Vue.set(this.pendingValidations, name, false);
                    }
                }
                this.publicErrors = {
                    ...this.errors,
                };
            },
            validate() {
                // this one validates fields not picked up by onValueChanged!
                // scenario when form is new and we immediately submit
                // but we want preserve possible async and already existing
                // errors (handled in onValueChanged)
                _forEach(this.fields, (field, name) => {
                    // this is a hack, we must refactor forms completely
                    if (field.clearError) {
                        this.clearError(name);
                    }

                    if (!this.errors[name]) {
                        this.validateField(field, field.value, name);
                    }
                });

                // we use this when form has conditional fields
                _forEach(this.errors, (value, name) => {
                    if (!this.fieldNames.includes(name)) {
                        this.clearError(name);
                    }
                });

                this.publicErrors = {
                    ...this.errors,
                };
            },
            // eslint-disable-next-line sonarjs/cognitive-complexity
            validateField(field, value, name) {
                let isValid = true;

                if (field.validate) {
                    for (let i = 0; i < field.validate.length; i += 1) {
                        const validationError = field.validate[i](value);
                        if (validationError) {
                            /* New design password validator returns a list of errors eg. min/max length,
                             * uppercase, lowercase etc.
                             */
                            if (Array.isArray(validationError)) {
                                isValid = false;
                                const validationErrorMessage = validationError.map((error) => {
                                    return this.$t(error.translationKey, error.payload);
                                }).join(', ');
                                this.$set(this.errors, name, capitalize(validationErrorMessage));
                            } else {
                                isValid = false;
                                this.$set(this.errors, name, capitalize(this.$t(validationError.translationKey, validationError.payload)));
                                break;
                            }
                        }
                    }
                }

                if (isValid) {
                    this.clearError(name);
                }
            },
            clearError(fieldName) {
                this.$delete(this.errors, fieldName);
            },
            // Powerful yet hacky method, will remove even async errors from a field
            // but there is no way to properly clear publicErrors right now
            clearFieldErrors(fieldName) {
                this.$delete(this.errors, fieldName);
                this.$delete(this.publicErrors, fieldName);
            },
        },
        components: {
            NonFieldErrors,
        },
    };
</script>
